import React, { useCallback } from "react";
import { 
    gql,
    useQuery
} from "@apollo/client";
import { 
    QueryFindAllCustomerAndMerchantOfferTransactionsArgs,
    Transaction, 
    TransactionPage
} from "../../../../schemaTypes";

export const findAllCustomerAndMerchantOfferTransactionsQuery = gql`
  query FindAllCustomerAndMerchantOfferTransactions(
    $customerUserId: String!
    $merchantUserId: String!
    $page: Int!
    $pageSize: Int!
    $statusFilter: String
    $typeFilter: String
    $searchText: String
    $sortBy: String
    $sortOrder: String
  ) {
    findAllCustomerAndMerchantOfferTransactions(
      customerUserId: $customerUserId
      merchantUserId: $merchantUserId
      page: $page
      pageSize: $pageSize
      statusFilter: $statusFilter
      typeFilter: $typeFilter
      searchText: $searchText
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      data {
        id
        amount
        charge
        reference
        type
        status
        currency
        transferTypes
        # wallet {
        #   id
        #   balance
        #   owner {
        #     id
        #     customer {
        #       firstname
        #       lastname
        #     }
        #   }
        #   createdAt
        #   updatedAt
        # }
        # recipientWallet {
        #   id
        #   balance
        #   currency
        #   owner {
        #     admin {
        #       merchantProfile {
        #         companyName
        #       }
        #     }
        #   }
        #   createdAt
        #   updatedAt
        # }
        bankPreference {
          id
        }
        offer {
          id
          title
          description
          category{
            name
          }
        }
        njoyCode
        partnerVoucher
        user {
          id
          # createdAt
          # updatedAt
        }
        createdAt
        updatedAt
      }
      total
      page
      pageSize
      totalPages
    }
  }
`;

export interface WithFindAllCustomerAndMerchantOfferTransactions {
  transactions: Transaction[];
  loading: boolean;
  loadMore: () => void;
  hasMoreTransactions: boolean;
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

interface Props {
  variables: QueryFindAllCustomerAndMerchantOfferTransactionsArgs;
  children: (data: WithFindAllCustomerAndMerchantOfferTransactions) => React.ReactElement | null;
}

export const FindAllCustomerAndMerchantOfferTransactionsController: React.FC<Props> = ({ children, variables }) => {
  const { data, loading, fetchMore } = useQuery<
    { findAllCustomerAndMerchantOfferTransactions: TransactionPage },
    QueryFindAllCustomerAndMerchantOfferTransactionsArgs
  >
  (findAllCustomerAndMerchantOfferTransactionsQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  const transactions = data?.findAllCustomerAndMerchantOfferTransactions?.data || [];
  const page = data?.findAllCustomerAndMerchantOfferTransactions?.page || variables.page;
  const pageSize = data?.findAllCustomerAndMerchantOfferTransactions?.pageSize || variables.pageSize;
  const total = data?.findAllCustomerAndMerchantOfferTransactions?.total || 0;
  const totalPages = data?.findAllCustomerAndMerchantOfferTransactions?.totalPages || 0;
  const hasMoreTransactions = page < totalPages;

  const loadMore = useCallback(() => {
    if (!hasMoreTransactions) return;
    fetchMore({
      variables: {
        ...variables,
        page: page + 1
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.findAllCustomerAndMerchantOfferTransactions) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          findAllCustomerAndMerchantOfferTransactions: {
            ...fetchMoreResult.findAllCustomerAndMerchantOfferTransactions,
            data: [
              ...(prev?.findAllCustomerAndMerchantOfferTransactions?.data || []),
              ...(fetchMoreResult.findAllCustomerAndMerchantOfferTransactions.data || [])
            ]
          }
        };
      }
    });
  }, [fetchMore, variables, page, hasMoreTransactions]);

  return children({
    transactions: transactions as any[],
    loading,
    loadMore,
    hasMoreTransactions,
    page,
    pageSize,
    total,
    totalPages,
  });
};